import {
  VERDICT_VALUES,
  type ExecutorKindValue,
  type SetKindValue,
  type SittingOutcomeValue,
} from "./vocabulary.generated";

export type Page<T> = {
  items: T[];
  total: number;
  /** Present when a full page was returned; pass back as ?cursor= for the next page. */
  next_cursor?: string | null;
};

/** qa-product-registry catalogs. Query params use these names (?feature=EXCESS_SOLAR). */
export type RegistryCatalog = "feature" | "hardware" | "software" | "program";

/** Canonical registry keys on a row, one list per catalog. */
export type TagSet = {
  features: string[];
  hardware: string[];
  software: string[];
  programs: string[];
};

export type RegistryEntry = {
  key: string;
  name: string;
  parent: string | null;
  status: string;
  is_family: boolean;
  group: string | null;
  level: string | null;
  description: string | null;
};

export type RegistryPayload = {
  version: string;
  activated: string | null;
  catalogs: Record<RegistryCatalog, RegistryEntry[]>;
  tag_fields: Record<RegistryCatalog, keyof TagSet>;
};

export type RegistryResolution = {
  input: string;
  catalog: RegistryCatalog;
  key: string | null;
  name: string | null;
  match_level: string;
  confidence: number;
  candidates: string[];
  expands_to: string[];
  registry_version: string;
};

export type TestedEvent = {
  at: string;
  run_id: string;
  run_number: number;
  outcome: string;
  spec_section_id: string;
  spec_version: string;
};

export type LastTested = {
  catalog: RegistryCatalog;
  input: string;
  key: string;
  name: string;
  matched_keys: string[];
  last_tested: TestedEvent | null;
  last_passed: TestedEvent | null;
  last_planned: {
    at: string;
    run_id: string;
    run_number: number;
    run_status: string;
    spec_section_id: string;
  } | null;
  matching_sections: string[];
  untagged_target_count: number;
  registry_version: string;
};

export type TagActivityItem = {
  key: string;
  name: string;
  parent: string | null;
  is_family: boolean;
  group: string | null;
  last_tested_at: string | null;
  last_passed_at: string | null;
  target_count: number;
};

export type TagActivity = {
  catalog: RegistryCatalog;
  items: TagActivityItem[];
  untagged_target_count: number;
  registry_version: string;
};

export type RunPolicy = {
  id: string;
  name: string;
  version: number;
};

/** A run's ownership row: who is doing what, with audit (Phase 5). */
export type Owner = {
  id: string;
  run_id: string;
  actor: string;
  role: "owner" | "runner" | "reviewer";
  assigned_by: string;
  source: "self" | "assign" | "system";
  assigned_at: string;
  created_at: string;
};

/** One active presence session: a person here, and what they are on. */
export type Presence = {
  id: string;
  actor: string;
  connection_id: string;
  surface: "web" | "mcp" | "api";
  run_id: string | null;
  scenario_id: string | null;
  last_seen_at: string;
  expires_at: string;
};

export type NotificationKind =
  | "verdict_review"
  | "spec_gap"
  | "run_assigned"
  | "human_sitting"
  | "physical_action"
  | "blocked_target";

export type NotificationState =
  | "pending"
  | "seen"
  | "delegated"
  | "acted"
  | "dismissed"
  | "expired";

/** The in-app inbox projection of a human task (Phase 5). */
export type Notification = {
  id: string;
  mc_task_id: string | null;
  actor: string | null;
  pool: string | null;
  kind: NotificationKind;
  priority: number;
  title: string;
  body: string | null;
  action_url: string | null;
  run_id: string | null;
  scenario_id: string | null;
  state: NotificationState;
  seen_at: string | null;
  acted_at: string | null;
  dismissed_at: string | null;
  delegated_at: string | null;
  resolution: Record<string, unknown> | null;
  due_at: string | null;
  expires_at: string | null;
  created_by: string;
  created_at: string;
};

/** A coverage target (case) a sitting scenario covers. */
export type SittingCase = {
  coverage_target_id: string;
  spec_section_id: string;
  spec_version: string;
  parameter_values?: Record<string, string> | null;
  interface_device: string | null;
};

/**
 * A run's human sitting: the checklist a person works through (Phase 5).
 *
 * Each checklist item is a scenario (`executor_kind=human`); it lists the cases
 * (coverage targets) it covers.
 */
export type SittingScenario = {
  scenario_id: string;
  origin: string;
  dispatch_status: string;
  runner_id: string | null;
  cases: SittingCase[];
  steps: unknown[];
  preconditions: unknown[];
  expected_outcome: unknown;
  estimated_duration_s: number | null;
  media: unknown[];
  outcome: string;
  results: {
    id: string;
    /** The stored per-scenario counter. */
    attempt_n: number;
    /** The attempt number people see: the rank among the case's attempts. */
    case_attempt_n: number | null;
    outcome: string;
    duration_ms: number | null;
    /** What the person noted, for a scenario answered in a sitting. */
    note: string | null;
    recorded_by: string | null;
    created_at: string;
  }[];
};

/** A person's answer for one scenario: Mission Control's `human_test` vocabulary. */
export type SittingOutcome = SittingOutcomeValue;

export type SittingAnswer = { scenario_id: string; outcome: SittingOutcome; note?: string | null };

export type HumanSitting = {
  run_id: string;
  run_number: number;
  scope_kind: string;
  scope_ref: string;
  status: string;
  estimated_duration_s: number | null;
  kit: unknown[];
  counts: Record<string, number>;
  scenarios: SittingScenario[];
};

export type Run = {
  id: string;
  run_number: number;
  created_by: string;
  scope_kind: string;
  scope_ref: string;
  spec_snapshot: Record<string, unknown>;
  exploration_mode: string;
  policy_id: string;
  policy: RunPolicy;
  status: string;
  trigger_type: string | null;
  summary: Record<string, unknown> | null;
  coverage_pct: number;
  go_no_go: string | null;
  /** Union of the run's coverage-target tags. */
  tags: TagSet;
  /** The run's own product area: registry keys from any catalog, set at plan time. */
  product_area: string[];
  created_at: string;
  /** When the run last changed status. */
  status_changed_at: string;
  /** When the run last saw any run-scoped activity. */
  last_activity_at: string;
  /** Derived: "live", "stale" (non-terminal but quiet past its policy threshold), or "ended". */
  liveness: string;
  /** Why an aborted run ended ("cancelled"/"failed"/"timed_out"/"superseded"); null otherwise. */
  terminal_reason: string | null;
  /** A short explanation of the terminal state, naming the actor for a reaped run. */
  terminal_detail: string | null;
  /** When the run entered a terminal state; null while non-terminal. */
  ended_at: string | null;
  /** Who is doing what: owner / runner / reviewer rows, with audit. */
  owners: Owner[];
  /** The policy standard the run is actually held to (policy definition + overrides). */
  effective_policy: Record<string, unknown> | null;
  /** Audit of per-run policy overrides. */
  policy_overrides: { actor: string; at: string; values: Record<string, unknown> }[];
  /** The randomness seed (§6.5). */
  seed: number | null;
  /** Run scope metadata (D3). */
  run_kind: string | null;
  priority: string | null;
  needed_by: string | null;
  emporia_environment: string | null;
  builds: Record<string, unknown> | null;
  selection_query: Record<string, unknown> | null;
  interface_devices: string[];
};

export type ReconcileItem = {
  run_id: string;
  run_number: number;
  tier: number;
  action: "complete" | "reduce" | "flag" | "orphan" | "skip";
  status: string;
  to_status: string | null;
  reason: string;
};

export type ReconcileReport = {
  dry_run: boolean;
  action: string;
  counts: {
    completed: number;
    reduced: number;
    flagged: number;
    orphaned: number;
    skipped: number;
  };
  items: ReconcileItem[];
};

export type CoverageTarget = {
  id: string;
  run_id: string;
  spec_section_id: string;
  spec_version: string;
  /** The case's full parameter set (`{name: value}`), or null when it has none. */
  parameter_values: Record<string, string> | null;
  /** Where the target is covered (`ios`, `android`, `linux-chrome`, …), or null. */
  interface_device: string | null;
  risk_weight: number | null;
  coverage_status: string;
  rationale: string | null;
  tags: TagSet;
  /** "caller" (supplied on write), "derived" (from the spec name), or null (untagged). */
  tag_source: string | null;
  registry_version: string | null;
  /** The test set this case runs in, and its position within it (null until assigned). */
  set_id: string | null;
  position: number | null;
};

/** What a test set is anchored on (docs/runs-and-test-sets.md §6.2). */
export type SetKind = SetKindValue;

/** One case in a test set: its identity and 1-based position within the set. */
export type SetCase = {
  id: string;
  spec_section_id: string;
  spec_version: string;
  parameter_values?: Record<string, string> | null;
  interface_device: string | null;
  position: number | null;
};

/** A test set: 2–10 cases from one run that share an environment. */
export type RunSet = {
  id: string;
  run_id: string;
  kind: SetKind;
  environment_signature: Record<string, unknown> | null;
  pinned: boolean;
  created_by: string;
  created_at: string;
  cases: SetCase[];
};

/** A case's pass history, recency-weighted (docs/runs-and-test-sets.md §6.7). */
export type CaseStability = {
  spec_section_id: string;
  /** The case's full parameter set, or null when it has none. */
  parameter_values: Record<string, string> | null;
  interface_device: string | null;
  attempts: number;
  passes: number;
  fails: number;
  flakes: number;
  weighted_pass_rate: number | null;
  /** stable / flaky / failing / unknown. */
  stability: string;
  last_outcome: string | null;
  last_attempt_at: string | null;
};

export type RunDetail = Run & {
  coverage_targets: CoverageTarget[];
  /** How the run's scenarios split across executors (D3): agent / human / mixed. */
  executor_counts: Record<string, number> | null;
};

export type ResultSummary = {
  id: string;
  /** The stored per-scenario counter. */
  attempt_n: number;
  /** The attempt number people see: the rank among the case's attempts. */
  case_attempt_n: number | null;
  outcome: string;
  duration_ms: number | null;
  /** What the attempt ran on (`ios`, `android`, `linux-chrome`, …), or null. */
  interface_device: string | null;
  /** The app/browser build the attempt ran against, or null. */
  build: string | null;
  /** The Emporia hardware under test (a registry key or model), or null. */
  emporia_device: string | null;
  /** Recorded, non-multiplying details (OS version, phone model, browser, headless). */
  attributes: Record<string, unknown> | null;
  /** Probe provenance: set when this attempt is a probe (investigation, not coverage). */
  probe_kind: string | null;
  classification: string | null;
  derived_from_result_id: string | null;
  created_at: string;
};

export type ScenarioDetail = {
  id: string;
  run_id: string;
  origin: string;
  definition: Record<string, unknown>;
  generated_against_spec_version: string;
  fixture_requirements: Record<string, unknown> | null;
  reservation_group_id: string | null;
  dispatch_status: string;
  runner_id: string | null;
  /** ``agent`` (default) or ``human``: who executes it. */
  executor_kind: ExecutorKindValue;
  /** The execution lease, renewed by heartbeat; null until dispatched. */
  lease_expires_at: string | null;
  created_at: string;
  coverage_target_ids: string[];
  results: ResultSummary[];
};

export type GoNoGo = {
  decision: string;
  reasons: string[];
};

/** One run's readiness: the decision, its reasons, and the four numbers. */
export type ReadinessNumbers = {
  run_id: string;
  run_number: number;
  created_at: string | null;
  scope_kind: string;
  scope_ref: string;
  status: string;
  decision: string;
  reasons: string[];
  coverage_pct: number;
  total_targets: number;
  covered_count: number;
  gaps_count: number;
  unresolved_regressions: number;
  blocked_count: number;
};

/** How the four numbers moved against the previous comparable run. */
export type ReadinessDelta = {
  coverage_pct: number;
  gaps_count: number;
  unresolved_regressions: number;
  blocked_count: number;
};

/** The band the Now screen leads with. */
export type Readiness = {
  current: ReadinessNumbers;
  previous: ReadinessNumbers | null;
  delta: ReadinessDelta | null;
};

/** Live per-run signal counts for a runs-list row. */
export type RunSignals = {
  run_id: string;
  coverage_pct: number;
  gaps_count: number;
  unresolved_regressions: number;
  blocked_count: number;
  decision: string;
};

export type AuthCheck = {
  user_id: string | null;
  email: string | null;
};

export type TriageVerdict = {
  id: string;
  result_id: string;
  verdict: string;
  spec_comparison: Record<string, unknown>;
  confidence: number | null;
  reasoning: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string | null;
};

export type PendingReview = TriageVerdict & {
  run_id: string;
  run_number: number;
  scenario_id: string;
  result_outcome: string;
};

/** A ranked triage queue item, carrying the inputs behind its rank. */
export type TriageQueueItem = PendingReview & {
  risk_weight: number;
  spec_section_ids: string[];
  scope_kind: string;
  run_go_no_go: string | null;
  release_blocking: boolean;
  priority: number;
};

export type TriageVerdictSummary = {
  id: string;
  verdict: string;
  confidence: number | null;
  reviewed_by: string | null;
  created_at: string;
};

export type ResultDetail = {
  id: string;
  scenario_id: string;
  /** The stored per-scenario counter. */
  attempt_n: number;
  /** The attempt number people see: the rank among the case's attempts. */
  case_attempt_n: number | null;
  /** Every case this result is an attempt at, each with its attempt number. */
  case_attempts: { coverage_target_id: string; attempt_n: number }[];
  outcome: string;
  artifacts_ref: Record<string, unknown> | null;
  observed_behavior: Record<string, unknown> | null;
  duration_ms: number | null;
  runner_metadata: Record<string, unknown> | null;
  /** What the attempt ran on (`ios`, `android`, `linux-chrome`, …), or null. */
  interface_device: string | null;
  /** The app/browser build the attempt ran against, or null. */
  build: string | null;
  /** The Emporia hardware under test (a registry key or model), or null. */
  emporia_device: string | null;
  /** Recorded, non-multiplying details (OS version, phone model, browser, headless). */
  attributes: Record<string, unknown> | null;
  /** Probe provenance: set when this attempt is a probe (investigation, not coverage). */
  probe_kind: string | null;
  classification: string | null;
  derived_from_result_id: string | null;
  created_at: string;
  triage_verdict: TriageVerdictSummary | null;
};

export type Gap = {
  spec_section_id: string;
  spec_version: string;
  parameter_values?: Record<string, string> | null;
  interface_device?: string | null;
  coverage_status: string;
  risk_weight: number | null;
  reason: string;
};

export type ReductionSummary = {
  coverage_pct: number;
  total_targets: number;
  status_counts: Record<string, number>;
  regressions: { total: number; unresolved: number; reviewed: number };
  gaps: Gap[];
  go_no_go: GoNoGo;
  policy: { name: string; version: number; min_coverage_pct: number };
  reduced_at: string | null;
};

export type Policy = {
  id: string;
  name: string;
  version: number;
  definition: Record<string, unknown>;
  created_by: string;
  is_active: boolean;
  created_at: string;
};

export type PolicyCreate = {
  name: string;
  definition: Record<string, unknown>;
  created_by: string;
};

export type PolicyUpdate = {
  is_active: boolean;
};

export type FindingType = "bug" | "spec_gap" | "noise";

export type Finding = {
  id: string;
  run_id: string;
  scenario_id: string | null;
  finding_type: FindingType;
  description: string;
  proposed_spec_section: string | null;
  graduated: boolean;
  graduated_at: string | null;
  created_by: string;
  created_at: string;
  tags: TagSet;
  registry_version: string | null;
};

export type FindingUpdate = {
  finding_type?: FindingType;
  proposed_spec_section?: string | null;
  graduate?: boolean;
};

export type FindingCreate = {
  finding_type: FindingType;
  description: string;
  created_by: string;
  scenario_id?: string | null;
  proposed_spec_section?: string | null;
};

/** One stage of the exploration intake funnel. */
export type FindingStage = {
  items: Finding[];
  total: number;
};

/** Exploration as a funnel: new findings → proposed additions → graduated. */
export type FindingFunnel = {
  new: FindingStage;
  proposed: FindingStage;
  graduated: FindingStage;
  backbone_growth: { since: string; count: number };
};

export type BumpedSection = {
  section_id: string;
  from_version: string;
  to_version: string;
};

export type ChangedSurface = {
  run_id: string;
  previous_run_id: string | null;
  new_sections: string[];
  bumped_sections: BumpedSection[];
};

export type ReportHeader = {
  run_number: number;
  scope_kind: string;
  scope_ref: string;
  snapshot_ref: string;
  policy_name: string;
  policy_version: number;
  generated_at: string;
  report_version: number;
  status: string;
  coverage_pct: number;
  gaps_count: number;
  unresolved_regressions: number;
  go_no_go: string;
  go_no_go_reasons: string[];
};

export type LiveReport = {
  content_markdown: string;
  header: ReportHeader;
};

export type PublishedReport = {
  id: string;
  run_id: string;
  report_version: number;
  verbosity: string;
  content_markdown: string;
  header: Record<string, unknown>;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
};

export type ReviewBatchResult = {
  items: TriageVerdict[];
  skipped_ids: string[];
};

export type OutcomeCounts = {
  pass: number;
  fail: number;
  error: number;
  flake: number;
  skipped: number;
};

export type CoverageTrendEntry = {
  run_id: string;
  run_number: number;
  created_at: string;
  scope_kind: string;
  coverage_pct: number;
  total_targets: number;
  covered_count: number;
  gap_count: number;
  unresolved_regressions: number;
  go_no_go: string | null;
  /** True when this run's spec snapshot differs from the run before it. */
  version_boundary: boolean;
  /** Coverage of just the tag-matching targets; present only with a tag filter. */
  tagged: { total_targets: number; covered_count: number; coverage_pct: number } | null;
};

export type SectionTrendEntry = {
  run_id: string;
  run_number: number;
  created_at: string;
  scope_kind: string;
  spec_version: string;
  interface_device: string | null;
  coverage_status: string;
  version_boundary: boolean;
  previous_version: string | null;
  outcome_counts: OutcomeCounts;
};

export type GreenWallCell = {
  coverage_status: string;
  spec_version: string;
  version_boundary: boolean;
  previous_version: string | null;
  outcome_counts: OutcomeCounts;
};

export type GreenWallRunCol = {
  run_id: string;
  run_number: number;
  created_at: string;
  scope_kind: string;
};

export type GreenWallRow = {
  spec_section_id: string;
  interface_device: string | null;
  cells: Record<string, GreenWallCell>;
};

export type GreenWallOut = {
  rows: GreenWallRow[];
  runs: GreenWallRunCol[];
  total: number;
};

export type TargetRef = {
  spec_section_id: string;
  interface_device: string | null;
  coverage_status: string;
  spec_version: string;
};

export type StatusChange = {
  spec_section_id: string;
  interface_device: string | null;
  from_status: string;
  to_status: string;
  from_version: string;
  to_version: string;
};

export type RunRef = {
  run_id: string;
  run_number: number;
  created_at: string;
};

export type RunCompareOut = {
  runs: { current: RunRef; baseline: RunRef };
  added: TargetRef[];
  removed: TargetRef[];
  status_changes: StatusChange[];
  regression_delta: {
    total: number;
    unresolved: number;
    current: { total: number; unresolved: number };
    baseline: { total: number; unresolved: number };
    new_regression_cases: TargetRef[];
    new_regression_sections: string[];
  };
};

export const VERDICT_TYPES = VERDICT_VALUES;

export const FINDING_TYPES = ["bug", "spec_gap", "noise"] as const;

export const RUN_DETAIL_TABS = ["overview", "triage", "exploration", "report"] as const;

export type RunDetailTab = (typeof RUN_DETAIL_TABS)[number];

/** Counts of runs by status and scope kind, for the runs-list facet chips. */
export type RunFacets = {
  status: Record<string, number>;
  scope_kind: Record<string, number>;
  total: number;
};

/** One group of command-palette search results. */
export type SearchRun = {
  id: string;
  run_number: number;
  scope_kind: string;
  scope_ref: string;
  status: string;
  go_no_go: string | null;
};

export type SearchResults = {
  runs: SearchRun[];
  sections: { spec_section_id: string }[];
  policies: { id: string; name: string; version: number; is_active: boolean }[];
  findings: {
    id: string;
    run_id: string;
    run_number: number;
    finding_type: string;
    graduated: boolean;
    description: string;
  }[];
  registry: { catalog: string; key: string; name: string }[];
};
